import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing Judge black card write-in system
 * Handles black card modal, skip tracking, czar timer, and black card submission
 */
const useBlackCardWriteIn = (socketEmit, roomCode, gameState, round, trackFeature) => {
  const [showBlackCardModal, setShowBlackCardModal] = useState(false);
  const [blackCardText, setBlackCardText] = useState('');
  const [czarWriteInTimer, setCzarWriteInTimer] = useState(0);
  const [hasSkippedThisRound, setHasSkippedThisRound] = useState(false);
  const czarTimerRef = useRef(null);

  // Reset skip tracking when round changes
  useEffect(() => {
    setHasSkippedThisRound(false);
  }, [round]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (czarTimerRef.current) {
        clearInterval(czarTimerRef.current);
        czarTimerRef.current = null;
      }
    };
  }, []);

  const closeBlackCardModal = useCallback(() => {
    setShowBlackCardModal(false);
    setBlackCardText('');
    setCzarWriteInTimer(0);

    if (czarTimerRef.current) {
      clearInterval(czarTimerRef.current);
      czarTimerRef.current = null;
    }
  }, []);

  const openBlackCardModal = useCallback(() => {
    setShowBlackCardModal(true);
    setBlackCardText('');

    // Start 30-second timer
    setCzarWriteInTimer(30);
    const startTime = Date.now();

    czarTimerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, 30 - elapsed);
      setCzarWriteInTimer(remaining);

      if (remaining === 0) {
        clearInterval(czarTimerRef.current);
        closeBlackCardModal();
      }
    }, 1000);
  }, [closeBlackCardModal]);

  const createCustomBlackCard = useCallback(() => {
    const trimmedText = blackCardText.trim();

    if (!trimmedText) {
      console.error('Question card text cannot be empty');
      return;
    }

    if (trimmedText.length > 150) {
      console.error('Question card text must be 150 characters or less');
      return;
    }

    socketEmit('create-black-card', {
      roomCode,
      blackCardText: trimmedText,
      // pickCount now determined automatically from underscore groups in text
    });

    closeBlackCardModal();
  }, [blackCardText, socketEmit, roomCode, closeBlackCardModal]);

  const skipBlackCard = useCallback(() => {
    socketEmit('skip-black-card', { roomCode });
    setHasSkippedThisRound(true);
  }, [socketEmit, roomCode]);

  const confirmBlackCard = useCallback(() => {
    socketEmit('czar-confirm-black-card', { roomCode });
  }, [socketEmit, roomCode]);

  return {
    showBlackCardModal,
    blackCardText,
    setBlackCardText,
    czarWriteInTimer,
    hasSkippedThisRound,
    setHasSkippedThisRound,
    czarTimerRef,
    openBlackCardModal,
    closeBlackCardModal,
    createCustomBlackCard,
    skipBlackCard,
    confirmBlackCard,
  };
};

export default useBlackCardWriteIn;
